import {
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { FC, useCallback, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { formatNumber } from '../../../../abelcommon';
import { TSummaryMachineInfo } from '../../../../abelcommon/hooks/useMachineInfo';
import { machineState } from '../../../../api';
import { TMinerDetail } from '../../../../api/types';
import { MachineListGeneralInfo } from './MachineListGeneralInfo';
import { loadBalancerAPI } from '../../../../api/load-balancer';
import { MountableLoadingOverlay } from '../../../../abelcommon/components/LoadingOverlay';
import { useTranslation } from 'react-i18next';

export enum MachineStatus {
  Online = 'Online',
  Offline = 'Offline',
  All = 'All',
}

const StatusColorMap = {
  [MachineStatus.Online]: 'success.main',
  [MachineStatus.Offline]: 'appRed.main',
};

interface IMachineListSectionProps {
  minerData: TMinerDetail[];
  summaryMachineInfo: TSummaryMachineInfo;
  onRefetchStatMiner: () => void;
}

export const MachineListSection: FC<IMachineListSectionProps> = ({
  minerData,
  summaryMachineInfo,
  onRefetchStatMiner,
}) => {
  const theme = useTheme();
  const isUpLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const [filterStatus, setFilterStatus] = useRecoilState(machineState);

  const { mutate: deleteStatMiner, isLoading: deleteStatMinerLoading } =
    loadBalancerAPI.hookWrapperMutation.useDeleteStatMiner();

  const handleSelectStatus = useCallback((e: any) => {
    setFilterStatus(e.target.value);
  }, []);

  const dataFiltered = useMemo(
    () =>
      minerData
        .filter(({ status }) => (filterStatus === MachineStatus.All ? true : status === filterStatus))
        .sort((a, b) => a.hostName.localeCompare(b.hostName)),
    [filterStatus, minerData]
  );
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader
        title={t('machineListSectionPage.machines')}
        sx={{ flexWrap: 'wrap' }}
        action={
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            {isUpLargeScreen && <MachineListGeneralInfo summaryMachineInfo={summaryMachineInfo} />}
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" color="appGray.contrastText">
                {t('machineListSectionPage.status')}
              </Typography>
              <Select value={filterStatus} onChange={handleSelectStatus} size="small" sx={{ minWidth: '100px' }}>
                <MenuItem value={MachineStatus.All}>{t('machineListSectionPage.all')}</MenuItem>
                <MenuItem value={MachineStatus.Online}>{t('machineListSectionPage.online')}</MenuItem>
                <MenuItem value={MachineStatus.Offline}>{t('machineListSectionPage.offline')}</MenuItem>
              </Select>
            </Stack>
          </Stack>
        }
      />
      <CardContent sx={{ overflow: 'auto', position: 'relative' }}>
        {!isUpLargeScreen && <MachineListGeneralInfo summaryMachineInfo={summaryMachineInfo} />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('machineListSectionPage.hostName')}</TableCell>
                <TableCell>{t('machineListSectionPage.hostIP')}</TableCell>
                <TableCell>{t('machineListSectionPage.cardType')}</TableCell>
                <TableCell>{t('machineListSectionPage.cardCount')}</TableCell>
                <TableCell>{t('machineListSectionPage.hashRate')} (Mh/s)</TableCell>
                <TableCell>{t('machineListSectionPage.status')}</TableCell>
                <TableCell>{t('machineListSectionPage.updatedAt')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataFiltered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                    {t('global.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                dataFiltered.map((row, index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {row.hostName}
                    </TableCell>
                    <TableCell>{row.hostIP}</TableCell>
                    <TableCell>{row.cardTypes}</TableCell>
                    <TableCell>{row.cardCounts}</TableCell>
                    <TableCell>{formatNumber(row.totalHashRate, { maximumFractionDigits: 2 })}</TableCell>
                    <TableCell>
                      <Typography
                        color={
                          row.status === MachineStatus.All
                            ? undefined
                            : StatusColorMap[row.status as MachineStatus.Online | MachineStatus.Offline]
                        }
                        sx={{ fontWeight: 500 }}
                      >
                        {t(`machineListSectionPage.${row.status.toLowerCase()}`)}
                      </Typography>
                    </TableCell>
                    <TableCell>{format(new Date(row.updatedAt * 1000), 'yyyy-MM-dd HH:mm:ss')}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          row.status !== MachineStatus.Offline
                            ? t('machineListSectionPage.cannotDeleteActiveMachine')
                            : null
                        }
                      >
                        <span>
                          <Button
                            variant="text"
                            color="error"
                            disabled={row.status !== MachineStatus.Offline}
                            onClick={() =>
                              row.status === MachineStatus.Offline &&
                              deleteStatMiner(
                                {
                                  keyStatMinerDetail: row.keyStatMinerDetail,
                                  statMinerKey: row.statMinerKey,
                                },
                                { onSuccess: onRefetchStatMiner }
                              )
                            }
                          >
                            {t('machineListSectionPage.delete')}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {deleteStatMinerLoading && <MountableLoadingOverlay />}
      </CardContent>
    </Card>
  );
};
